import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, ChevronRight, Download, Users, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time penalty calculation
const getTimePenalty = (seconds) => {
  if (seconds <= 190) return { penalty: 0, label: "No Penalty" };
  if (seconds <= 200) return { penalty: 0.5, label: "-0.5" };
  if (seconds <= 210) return { penalty: 1.0, label: "-1.0" };
  if (seconds <= 220) return { penalty: 1.5, label: "-1.5" };
  if (seconds <= 230) return { penalty: 2.0, label: "-2.0" };
  if (seconds <= 240) return { penalty: 2.5, label: "-2.5" };
  if (seconds <= 250) return { penalty: 3.0, label: "-3.0" };
  return { penalty: 0, label: "ELIMINATED", eliminated: true };
};

// Format time display
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);
  return `${mins}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
};

export default function Dashboard() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  
  const [competition, setCompetition] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Timer state
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  
  // Score entry state
  const [currentPoetIndex, setCurrentPoetIndex] = useState(0);
  const [scores, setScores] = useState(["", "", "", "", ""]);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [submittedScore, setSubmittedScore] = useState(null);
  const [advanceCount, setAdvanceCount] = useState("");
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);

  // Fetch competition data
  const fetchCompetition = useCallback(async () => {
    if (!competitionId) {
      setLoading(false);
      return;
    }
    try {
      const [compRes, standingsRes] = await Promise.all([
        axios.get(`${API}/competitions/${competitionId}`),
        axios.get(`${API}/competitions/${competitionId}/standings`)
      ]);
      setCompetition(compRes.data);
      setStandings(standingsRes.data.standings || []);
    } catch (error) {
      console.error("Error fetching competition:", error);
      toast.error("Failed to load competition");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchCompetition();
  }, [fetchCompetition]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setTime((prev) => prev + 0.01);
      }, 10);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  // Get timer color class
  const getTimerClass = () => {
    if (time <= 180) return "timer-normal";
    if (time <= 190) return "timer-grace";
    return "timer-penalty";
  };

  // Get current active poets (not eliminated, advancing)
  const getActivePoets = () => {
    if (!competition?.poets) return [];
    return competition.poets.filter(p => p.is_advancing && !p.is_barometer);
  };

  const currentPoet = getActivePoets()[currentPoetIndex];
  const barometer = competition?.poets?.find(p => p.is_barometer);

  // Score handlers
  const handleScoreChange = (index, value) => {
    const newScores = [...scores];
    const numValue = value.replace(/[^0-9.]/g, "");
    if (numValue === "" || (parseFloat(numValue) >= 0 && parseFloat(numValue) <= 10)) {
      newScores[index] = numValue;
      setScores(newScores);
    }
  };

  const handleScoreKeyDown = (index, e) => {
    if (e.key === "Tab" || e.key === "Enter") {
      if (index < 4) {
        e.preventDefault();
        document.getElementById(`score-${index + 1}`)?.focus();
      }
    }
  };

  const calculateDisplayScore = () => {
    const numScores = scores.map(s => parseFloat(s) || 0);
    if (numScores.filter(s => s > 0).length < 5) return null;
    const sorted = [...numScores].sort((a, b) => a - b);
    const middle = sorted.slice(1, 4);
    return middle.reduce((a, b) => a + b, 0);
  };

  const submitScore = async () => {
    if (!currentPoet || !competition) return;
    
    const numScores = scores.map(s => parseFloat(s) || 0);
    if (numScores.some(s => s === 0)) {
      toast.error("Please enter all 5 scores");
      return;
    }

    try {
      const response = await axios.post(`${API}/competitions/${competitionId}/scores`, {
        poet_id: currentPoet.id,
        round_number: competition.current_round,
        judge_scores: numScores,
        time_seconds: time
      });
      
      setSubmittedScore(response.data);
      setShowScoreDialog(true);
      
      // Reset for next poet
      setScores(["", "", "", "", ""]);
      setTime(0);
      setIsRunning(false);
      
      // Move to next poet
      const activePoets = getActivePoets();
      if (currentPoetIndex < activePoets.length - 1) {
        setCurrentPoetIndex(prev => prev + 1);
      }
      
      fetchCompetition();
      toast.success(`Score submitted for ${currentPoet.name}`);
    } catch (error) {
      console.error("Error submitting score:", error);
      toast.error(error.response?.data?.detail || "Failed to submit score");
    }
  };

  const startRound = async () => {
    if (!competitionId) return;
    try {
      await axios.post(`${API}/competitions/${competitionId}/start-round`);
      setCurrentPoetIndex(0);
      fetchCompetition();
      toast.success("Round started!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start round");
    }
  };

  const advancePoets = async (customCount = null) => {
    if (!competition) return;
    
    const round = competition.current_round;
    let count = customCount;
    
    // Round 1 and 2 require user to specify count
    if ((round === 1 || round === 2) && !count) {
      setShowAdvanceDialog(true);
      return;
    }
    
    try {
      const payload = { round_number: round };
      if ((round === 1 || round === 2) && count) {
        payload.advance_count = parseInt(count);
      }
      
      await axios.post(`${API}/competitions/${competitionId}/advance`, payload);
      setShowAdvanceDialog(false);
      setAdvanceCount("");
      fetchCompetition();
      toast.success("Poets advanced to next round!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to advance poets");
    }
  };

  const exportResults = () => {
    if (!competitionId) return;
    window.open(`${API}/competitions/${competitionId}/export/csv`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-heading text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!competitionId) {
    return (
      <div className="text-center py-16" data-testid="no-competition-state">
        <Clock className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
        <h2 className="font-heading font-bold text-2xl mb-4">No Active Competition</h2>
        <p className="text-muted-foreground mb-8">Create a new competition or select one from history.</p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => navigate("/setup")} data-testid="create-competition-btn">
            Create Competition
          </Button>
          <Button variant="outline" onClick={() => navigate("/history")} data-testid="view-history-btn">
            View History
          </Button>
        </div>
      </div>
    );
  }

  const penaltyInfo = getTimePenalty(time);
  const roundLabels = { 1: "ROUND 1", 2: "ROUND 2", 3: "FINALS" };

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight" data-testid="competition-name">
            {competition?.name}
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-1" data-testid="competition-status">
            {competition?.status === "setup" ? "SETUP" : roundLabels[competition?.current_round] || competition?.status?.toUpperCase()}
          </p>
        </div>
        <div className="flex gap-3">
          {competition?.status !== "setup" && (
            <Button variant="outline" onClick={exportResults} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Setup state */}
      {competition?.status === "setup" && (
        <Card data-testid="setup-card">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading font-semibold text-xl mb-2">Competition Ready</h3>
            <p className="text-muted-foreground mb-6">
              {competition?.poets?.length || 0} poets registered
              {barometer && " (including barometer)"}
            </p>
            <Button onClick={startRound} disabled={!competition?.poets?.length} data-testid="start-round-btn">
              Start Round 1
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active competition */}
      {competition?.status !== "setup" && competition?.status !== "completed" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Timer & Score Entry - Left Column */}
          <div className="lg:col-span-7 space-y-6">
            {/* Current Poet */}
            <Card data-testid="current-poet-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
                  Now Performing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-bold" data-testid="current-poet-name">
                  {currentPoet?.name || "Select a poet"}
                </div>
                <p className="font-mono text-sm text-muted-foreground mt-1">
                  Poet {currentPoetIndex + 1} of {getActivePoets().length}
                </p>
              </CardContent>
            </Card>

            {/* Timer */}
            <Card data-testid="timer-card">
              <CardContent className="py-8">
                <div className={`timer-display text-7xl md:text-8xl text-center ${getTimerClass()}`} data-testid="timer-display">
                  {formatTime(time)}
                </div>
                
                {/* Penalty indicator */}
                <div className="text-center mt-4" data-testid="penalty-indicator">
                  {time > 190 && (
                    <span className={`font-mono text-lg ${penaltyInfo.eliminated ? "text-red-600 font-bold" : "text-red-500"}`}>
                      {penaltyInfo.eliminated ? "AUTOMATIC ELIMINATION" : `Time Penalty: ${penaltyInfo.label}`}
                    </span>
                  )}
                  {time > 180 && time <= 190 && (
                    <span className="font-mono text-lg text-orange-500">Grace Period</span>
                  )}
                </div>

                {/* Timer controls */}
                <div className="flex justify-center gap-4 mt-6">
                  <Button
                    size="lg"
                    onClick={() => setIsRunning(!isRunning)}
                    className="w-32"
                    data-testid={isRunning ? "pause-timer-btn" : "start-timer-btn"}
                  >
                    {isRunning ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                    {isRunning ? "Pause" : "Start"}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => { setTime(0); setIsRunning(false); }}
                    data-testid="reset-timer-btn"
                  >
                    <RotateCcw className="w-5 h-5 mr-2" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Score Entry */}
            <Card data-testid="score-entry-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
                  Judge Scores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4 mb-6">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <div key={index} className="text-center">
                      <label className="font-heading text-xs text-muted-foreground mb-2 block">
                        Judge {index + 1}
                      </label>
                      <Input
                        id={`score-${index}`}
                        type="text"
                        inputMode="decimal"
                        value={scores[index]}
                        onChange={(e) => handleScoreChange(index, e.target.value)}
                        onKeyDown={(e) => handleScoreKeyDown(index, e)}
                        className="text-center font-mono text-xl h-14"
                        placeholder="0.0"
                        data-testid={`score-input-${index}`}
                      />
                    </div>
                  ))}
                </div>

                {/* Calculated score preview */}
                {calculateDisplayScore() !== null && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-heading text-sm text-muted-foreground">Raw Score:</span>
                      <span className="font-mono text-2xl font-bold" data-testid="calculated-score">
                        {calculateDisplayScore().toFixed(1)}
                      </span>
                    </div>
                    {penaltyInfo.penalty > 0 && !penaltyInfo.eliminated && (
                      <div className="flex justify-between items-center mt-2">
                        <span className="font-heading text-sm text-red-500">After Penalty:</span>
                        <span className="font-mono text-xl text-red-500" data-testid="final-score-preview">
                          {(calculateDisplayScore() - penaltyInfo.penalty).toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <Button 
                  onClick={submitScore} 
                  className="w-full mt-6" 
                  size="lg"
                  disabled={!currentPoet || scores.some(s => s === "")}
                  data-testid="submit-score-btn"
                >
                  Submit Score
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Standings - Right Column */}
          <div className="lg:col-span-5">
            <Card className="h-fit" data-testid="standings-card">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
                  Current Standings
                </CardTitle>
                <span className="font-mono text-xs text-muted-foreground">
                  {competition?.current_round === 1 
                    ? "You choose who advances" 
                    : competition?.current_round === 2 
                      ? "You choose finalists"
                      : "Top 3 win"}
                </span>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Poet</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                      <TableHead className="w-20">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((poet, index) => (
                      <TableRow 
                        key={poet.id}
                        className={poet.id === currentPoet?.id ? "bg-muted" : ""}
                        data-testid={`standing-row-${index}`}
                      >
                        <TableCell className="font-mono font-bold">{poet.rank}</TableCell>
                        <TableCell className="font-heading font-medium">{poet.name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {poet.total_score.toFixed(1)}
                        </TableCell>
                        <TableCell>
                          {poet.elimination_round ? (
                            <span className="badge badge-eliminated">OUT</span>
                          ) : poet.is_advancing ? (
                            <span className="badge badge-advancing">IN</span>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Barometer display */}
            {barometer && (
              <Card className="mt-4" data-testid="barometer-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
                    Barometer Poet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <span className="font-heading font-medium">{barometer.name}</span>
                    <span className="badge badge-barometer">BAR</span>
                  </div>
                  {barometer.round_scores?.length > 0 && (
                    <p className="font-mono text-sm text-muted-foreground mt-2">
                      Score: {barometer.total_score?.toFixed(1) || "—"}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Round controls */}
            <Card className="mt-4" data-testid="round-controls-card">
              <CardContent className="py-4">
                {competition?.current_round === 1 ? (
                  <Button 
                    onClick={() => advancePoets()} 
                    className="w-full"
                    variant="outline"
                    data-testid="advance-poets-btn"
                  >
                    End Round 1 & Choose Advancing Poets
                  </Button>
                ) : competition?.current_round === 2 ? (
                  <Button 
                    onClick={() => advancePoets()} 
                    className="w-full"
                    variant="outline"
                    data-testid="advance-poets-btn"
                  >
                    End Round 2 & Choose Finalists
                  </Button>
                ) : (
                  <Button 
                    onClick={() => advancePoets()} 
                    className="w-full"
                    variant="outline"
                    data-testid="advance-poets-btn"
                  >
                    End Finals & Declare Winners
                  </Button>
                )}
                {competition?.current_round < 3 && (
                  <Button 
                    onClick={startRound} 
                    className="w-full mt-2"
                    data-testid="next-round-btn"
                  >
                    Start Round {competition?.current_round + 1}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Completed state */}
      {competition?.status === "completed" && (
        <Card data-testid="completed-card">
          <CardContent className="py-12 text-center">
            <h3 className="font-heading font-bold text-3xl mb-6">Competition Complete!</h3>
            
            {/* Winners */}
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
              {standings.slice(0, 3).map((poet, index) => (
                <div key={poet.id} className={`p-6 border ${index === 0 ? "border-2 border-black" : ""}`}>
                  <div className="font-mono text-4xl font-bold mb-2">{index + 1}</div>
                  <div className="font-heading font-semibold text-lg">{poet.name}</div>
                  <div className="font-mono text-muted-foreground">{poet.total_score.toFixed(1)}</div>
                </div>
              ))}
            </div>
            
            <Button onClick={exportResults} data-testid="export-final-btn">
              <Download className="w-4 h-4 mr-2" />
              Export Full Results
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Score submission dialog */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent data-testid="score-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Score Submitted</DialogTitle>
          </DialogHeader>
          {submittedScore && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Raw Score</p>
                  <p className="font-mono text-2xl font-bold">{submittedScore.raw_score.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Final Score</p>
                  <p className="font-mono text-2xl font-bold">{submittedScore.final_score.toFixed(1)}</p>
                </div>
              </div>
              
              {submittedScore.time_penalty > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-red-600 font-mono">
                    Time Penalty: -{submittedScore.time_penalty.toFixed(1)}
                  </p>
                </div>
              )}
              
              {submittedScore.is_eliminated && (
                <div className="p-3 bg-red-100 border border-red-300 rounded">
                  <p className="text-red-700 font-bold">ELIMINATED - Over time limit</p>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Score Breakdown</p>
                <div className="flex gap-2 font-mono">
                  <span className="line-through text-muted-foreground">{submittedScore.dropped_low}</span>
                  {submittedScore.counted_scores.map((s, i) => (
                    <span key={i} className="font-semibold">{s}</span>
                  ))}
                  <span className="line-through text-muted-foreground">{submittedScore.dropped_high}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowScoreDialog(false)} data-testid="close-score-dialog-btn">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance poets dialog (Round 1 & 2) */}
      <Dialog open={showAdvanceDialog} onOpenChange={setShowAdvanceDialog}>
        <DialogContent data-testid="advance-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              End Round {competition?.current_round}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">
              {getActivePoets().length} poets competed in Round {competition?.current_round}. 
              How many should advance to {competition?.current_round === 1 ? "Round 2" : "Finals"}?
            </p>
            <div>
              <label className="font-heading text-sm text-muted-foreground mb-2 block">
                Number of poets to advance (minimum 3)
              </label>
              <Input
                type="number"
                min="3"
                max={getActivePoets().length}
                value={advanceCount}
                onChange={(e) => setAdvanceCount(e.target.value)}
                placeholder={`3 - ${getActivePoets().length}`}
                className="font-mono text-lg"
                data-testid="advance-count-input"
              />
            </div>
            {competition?.current_round === 1 && (
              <p className="text-sm text-muted-foreground">
                Note: In Round 2, you will choose again how many advance to Finals. Top 3 in Finals compete for 1st, 2nd, 3rd.
              </p>
            )}
            {competition?.current_round === 2 && (
              <p className="text-sm text-muted-foreground">
                Note: Top 3 in Finals will compete for 1st, 2nd, and 3rd place.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvanceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => advancePoets(advanceCount)}
              disabled={!advanceCount || parseInt(advanceCount) < 3 || parseInt(advanceCount) > getActivePoets().length}
              data-testid="confirm-advance-btn"
            >
              Advance {advanceCount || "?"} Poets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
